import { Input as AntInput, Modal, Select as AntSelect, Table } from "antd";
import Button from "components/shared/button";
import { Popup } from "components/popups/Popup";
import Input from "components/shared/inputs/input";
import Select from "components/shared/inputs/select";
import useTranslation from "utils/hooks/useTranslation";
import useCustomForm from "utils/hooks/useCustomForm";
import React, { useEffect, useMemo, useState } from "react";
import {
  useCreateHeatpumpDocMutation,
  useDeleteHeatpumpDocMutation,
  useLazyGetAllAccessoriesQuery,
  useUpdateHeatpumpDocMutation,
  useUpdateHeatpumpMutation,
} from "store/api";
import type { DatasourceMutationRequest } from "./TvaPopup";

type TableRecord = Record<string, any>;

type AccessoryOption = {
  id: string;
  code: string;
  reference: string;
  label: string;
};

type HpDocRecord = {
  id?: number | string;
  hp?: number | string;
  title?: string;
  link?: string;
  category?: string;
  environnement_id?: number | string;
};

type HpEditPopupProps = {
  isOpen: boolean;
  onClose: () => void;
  request: DatasourceMutationRequest | null;
  row?: TableRecord | null;
  envNumeric?: number;
  lang: string;
  onSaved?: () => Promise<void> | void;
};

const HP_DOC_CATEGORY_OPTIONS = [
  { value: ".pdf", label: ".pdf" },
  { value: ".odt", label: ".odt" },
];

const DETAIL_FIELDS = [
  "heatpump_type",
  "heatpump_DHWtype",
  "heatpump_cooling",
  "heatpump_strength",
  "heatpump_energyclass55",
  "heatpump_eta55",
  "heatpump_scop55",
  "heatpump_energyclass35",
  "heatpump_eta35",
  "heatpump_scop35",
  "heatpump_acousticUE",
  "heatpump_acousticUI",
  "heatpump_IHM",
  "heatpump_connectivity",
  "heatpump_thermostat",
  "heatpump_expansioncapacity",
  "heatpump_installationcapacity",
  "heatpump_DHWcapacitytank",
  "heatpump_DHWmaterialtank",
  "heatpump_DHWenergyclasstank",
  "heatpump_DHWcop",
  "heatpump_dimensionUE",
  "heatpump_weightUE",
  "heatpump_dimensionUI",
  "heatpump_weightUI",
  "heatpump_distanceUIUE",
  "heatpump_dropUIUE",
  "heatpump_diameterlink",
] as const;

const toRecommendedNumber = (value: string) => (value === "true" ? 1 : 0);
const toYesNoString = (value: unknown) => {
  if (value === true || value === 1 || value === "1" || String(value).toLowerCase() === "true") {
    return "true";
  }
  return "false";
};

const toCompatibleIds = (value: unknown): string[] => {
  if (!Array.isArray(value)) return [];
  return value.map(String);
};

const toNumberIfNumeric = (value: string): string | number => {
  if (/^\d+$/.test(value)) return Number(value);
  return value;
};

const toNullable = (value: unknown): unknown => {
  if (value === "" || value === undefined) return null;
  return value;
};

const DETAIL_FIELDS_MIDPOINT = Math.ceil(DETAIL_FIELDS.length / 2);
const DETAIL_FIELDS_LEFT = DETAIL_FIELDS.slice(0, DETAIL_FIELDS_MIDPOINT);
const DETAIL_FIELDS_RIGHT = DETAIL_FIELDS.slice(DETAIL_FIELDS_MIDPOINT);

const HpEditPopup: React.FC<HpEditPopupProps> = ({
  isOpen,
  onClose,
  request,
  row,
  envNumeric,
  lang,
  onSaved,
}) => {
  const { t } = useTranslation();
  const yesNoOptions = useMemo(
    () => [
      { value: "true", text: t("yes") },
      { value: "false", text: t("no") },
    ],
    [t]
  );
  const [isSaving, setIsSaving] = useState(false);
  const [showCompatibleSelector, setShowCompatibleSelector] = useState(false);
  const [compatibleAccessoryIds, setCompatibleAccessoryIds] = useState<string[]>([]);
  const [allAccessories, setAllAccessories] = useState<AccessoryOption[]>([]);
  const [hpDocs, setHpDocs] = useState<HpDocRecord[]>([]);
  const [isHpDocModalOpen, setIsHpDocModalOpen] = useState(false);
  const [currentHpDoc, setCurrentHpDoc] = useState<HpDocRecord | null>(null);
  const [hpDocErrors, setHpDocErrors] = useState<{ title?: string; link?: string; category?: string }>({});
  const [updateHeatpump] = useUpdateHeatpumpMutation();
  const [fetchAllAccessories] = useLazyGetAllAccessoriesQuery();
  const [createHeatpumpDoc, { isLoading: isCreatingHpDoc }] = useCreateHeatpumpDocMutation();
  const [updateHeatpumpDoc, { isLoading: isUpdatingHpDoc }] = useUpdateHeatpumpDocMutation();
  const [deleteHeatpumpDoc, { isLoading: isDeletingHpDoc }] = useDeleteHeatpumpDocMutation();

  const { control, reset, getValues, setError, clearErrors } = useCustomForm<{
    heatpump_reference: string;
    heatpump_code: string;
    heatpump_price: string;
    heatpump_ecoprice: string;
    heatpump_photo: string;
    regulation_hp: string;
    regulation_hp_s: string;
    regulation_hp_t: string;
    regulation_hp_s_t: string;
    heatpump_recommended: string;
    heatpump_prio: string;
    elec_schema: string;
    hydrau_schema: string;
    certif_hp_k: string;
    photo_ambiance: string;
    prod_europ: string;
    top_right_synth: string;
    picto_1_url: string;
    picto_1_name: string;
    picto_2_url: string;
    picto_2_name: string;
    picto_3_url: string;
    picto_3_name: string;
    picto_4_url: string;
    picto_4_name: string;
    adem_modele_pac: string;
    adem_agrement_modele: string;
    regulation_mode: string;
    description_synthese: string;
    description_bc: string;
    description: string;
    [key: string]: string;
  }>({
    defaultValues: {
      heatpump_reference: "",
      heatpump_code: "",
      heatpump_price: "",
      heatpump_ecoprice: "",
      heatpump_photo: "",
      regulation_hp: "",
      regulation_hp_s: "",
      regulation_hp_t: "",
      regulation_hp_s_t: "",
      heatpump_recommended: "false",
      certif_hp_k: "",
      photo_ambiance: "",
      prod_europ: "",
      top_right_synth: "",
      picto_1_url: "",
      picto_1_name: "",
      picto_2_url: "",
      picto_2_name: "",
      picto_3_url: "",
      picto_3_name: "",
      picto_4_url: "",
      picto_4_name: "",
      adem_modele_pac: "",
      adem_agrement_modele: "",
      regulation_mode: "",
      description_synthese: "",
      description_bc: "",
      heatpump_prio: "",
      elec_schema: "",
      hydrau_schema: "",
      description: "",
      ...Object.fromEntries(DETAIL_FIELDS.map((field) => [field, ""])),
    },
  });

  useEffect(() => {
    if (!isOpen) return;

    setIsSaving(false);
    setShowCompatibleSelector(false);
    setHpDocErrors({});

    const nextValues: Record<string, string> = {
      heatpump_reference: String(row?.heatpump_reference ?? ""),
      heatpump_code: String(row?.heatpump_code ?? ""),
      heatpump_price: String(row?.heatpump_price ?? ""),
      heatpump_ecoprice: String(row?.heatpump_ecoprice ?? ""),
      heatpump_photo: String(row?.heatpump_photo ?? ""),
      regulation_hp: String(row?.regulation_hp ?? ""),
      regulation_hp_s: String(row?.regulation_hp_s ?? ""),
      regulation_hp_t: String(row?.regulation_hp_t ?? ""),
      regulation_hp_s_t: String(row?.regulation_hp_s_t ?? ""),
      heatpump_recommended: toYesNoString(row?.heatpump_recommended),
      heatpump_prio: String(row?.heatpump_prio ?? ""),
      elec_schema: String(row?.elec_schema ?? ""),
      hydrau_schema: String(row?.hydrau_schema ?? ""),
      certif_hp_k: String(row?.certif_hp_k ?? ""),
      photo_ambiance: String(row?.photo_ambiance ?? ""),
      prod_europ: String(row?.prod_europ ?? ""),
      top_right_synth: String(row?.top_right_synth ?? ""),
      picto_1_url: String(row?.picto_1_url ?? ""),
      picto_1_name: String(row?.picto_1_name ?? ""),
      picto_2_url: String(row?.picto_2_url ?? ""),
      picto_2_name: String(row?.picto_2_name ?? ""),
      picto_3_url: String(row?.picto_3_url ?? ""),
      picto_3_name: String(row?.picto_3_name ?? ""),
      picto_4_url: String(row?.picto_4_url ?? ""),
      picto_4_name: String(row?.picto_4_name ?? ""),
      adem_modele_pac: String(row?.adem_modele_pac ?? ""),
      adem_agrement_modele: String(row?.adem_agrement_modele ?? ""),
      regulation_mode: String(row?.regulation_mode ?? ""),
      description_synthese: String(row?.description_synthese ?? ""),
      description_bc: String(row?.description_bc ?? ""),
      description: String(row?.description ?? ""),
    };

    DETAIL_FIELDS.forEach((field) => {
      nextValues[field] = String(row?.[field] ?? "");
    });

    reset(nextValues as any);
    setCompatibleAccessoryIds(toCompatibleIds(row?.acc_compatible));
  setHpDocs(Array.isArray(row?.hpdocs) ? row.hpdocs : []);

    fetchAllAccessories()
      .unwrap()
      .then((items) => {
        const mapped = (Array.isArray(items) ? items : []).map((item: any) => ({
          id: String(item.id),
          code: String(item.code ?? ""),
          reference: String(item.reference_def ?? ""),
          label: `${String(item.code ?? "")} ${String(item.reference_def ?? "")}`.trim(),
        }));
        setAllAccessories(mapped);
      })
      .catch(() => {
        setAllAccessories([]);
      });
  }, [fetchAllAccessories, isOpen, reset, row]);

  const compatibleRows = useMemo(() => {
    const byId = new Map(allAccessories.map((item) => [item.id, item]));
    return compatibleAccessoryIds.map((id) => {
      const found = byId.get(id);
      return {
        key: id,
        id,
        code: found?.code ?? "",
        reference: found?.reference ?? "",
      };
    });
  }, [allAccessories, compatibleAccessoryIds]);

  const availableAccessoryOptions = useMemo(() => {
    return allAccessories.map((item) => ({ value: item.id, label: item.label }));
  }, [allAccessories]);

  const handleDeleteCompatible = (id: string) => {
    setCompatibleAccessoryIds((prev) => prev.filter((x) => x !== id));
  };

  const openCreateHpDoc = () => {
    const rowId = row?.id ?? row?.key;
    setHpDocErrors({});
    setCurrentHpDoc({ hp: rowId });
    setIsHpDocModalOpen(true);
  };

  const openEditHpDoc = (doc: HpDocRecord) => {
    setHpDocErrors({});
    setCurrentHpDoc({ ...doc });
    setIsHpDocModalOpen(true);
  };

  const closeHpDocModal = () => {
    setIsHpDocModalOpen(false);
    setCurrentHpDoc(null);
    setHpDocErrors({});
  };

  const handleDeleteHpDoc = async (docId: number | string | undefined) => {
    if (docId == null) return;

    try {
      await deleteHeatpumpDoc({ id: docId }).unwrap();
      setHpDocs((prev) => prev.filter((doc) => String(doc.id) !== String(docId)));
    } catch {
      // handled by api interceptor
    }
  };

  const handleHpDocSubmit = async () => {
    if (!currentHpDoc) return;
    const isEditHpDoc = currentHpDoc.id != null;

    const title = String(currentHpDoc.title ?? "").trim();
    const link = String(currentHpDoc.link ?? "").trim();
    const category = String(currentHpDoc.category ?? "").trim();

    const nextErrors: { title?: string; link?: string; category?: string } = {};
    if (!title) nextErrors.title = t("TITLE_ERROR");
    if (!link) nextErrors.link = t("LINK_ERROR");
    if (!category) nextErrors.category = t("TYPE_ERROR");
    setHpDocErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    const payload: Record<string, unknown> = {
      ...currentHpDoc,
      title,
      link,
      category,
    };

    try {
      if (isEditHpDoc && currentHpDoc.id != null) {
        payload.env = currentHpDoc.environnement_id;
        const updated = await updateHeatpumpDoc({ id: currentHpDoc.id, data: payload }).unwrap();
        setHpDocs((prev) => {
          const filtered = prev.filter((doc) => String(doc.id) !== String(currentHpDoc.id));
          return [...filtered, updated as HpDocRecord];
        });
      } else {
        const rowId = row?.id ?? row?.key;
        if (rowId == null) return;
        payload.lang = lang;
        payload.env = envNumeric;
        const created = await createHeatpumpDoc({ heatpumpId: rowId, data: payload }).unwrap();
        setHpDocs((prev) => [...prev, created as HpDocRecord]);
      }
      closeHpDocModal();
    } catch {
      // handled by api interceptor
    }
  };

  const handleSave = async () => {
    if (!request) return;
    const rowId = row?.id ?? row?.key;
    if (rowId == null) return;

    const values = getValues();
    const normalizedDescription = String(values.description ?? "").trim();
    if (!normalizedDescription) {
      setError("description", { type: "required", message: t("Required") });
      return;
    }
    clearErrors(["description"]);

    const detailPayload = DETAIL_FIELDS.reduce<Record<string, unknown>>((acc, field) => {
      acc[field] = toNullable(String(values[field] ?? ""));
      return acc;
    }, {});

    const baseUrl = request.url.replace(/\/[^/]+$/, "");
    const url = `${baseUrl}/${rowId}`;

    const payload: Record<string, unknown> = {
      id: rowId,
      heatpump_reference: values.heatpump_reference,
      heatpump_code: values.heatpump_code,
      reference_def: row?.reference_def,
      heatpump_price: values.heatpump_price,
      heatpump_ecoprice: values.heatpump_ecoprice,
      description: normalizedDescription,
      code: row?.code,
      heatpump_photo: values.heatpump_photo,
      regulation_hp: values.regulation_hp,
      regulation_hp_s: values.regulation_hp_s,
      regulation_hp_t: values.regulation_hp_t,
      regulation_hp_s_t: values.regulation_hp_s_t,
      heatpump_recommended: toRecommendedNumber(values.heatpump_recommended),
      heatpump_prio: toNullable(values.heatpump_prio),
      elec_schema: values.elec_schema,
      hydrau_schema: values.hydrau_schema,
      acc_compatible: compatibleAccessoryIds.map(toNumberIfNumeric),
      certif_hp_k: values.certif_hp_k,
      photo_ambiance: values.photo_ambiance,
      prod_europ: toNullable(values.prod_europ),
      top_right_synth: values.top_right_synth,
      picto_1_url: values.picto_1_url,
      picto_1_name: values.picto_1_name,
      picto_2_url: values.picto_2_url,
      picto_2_name: values.picto_2_name,
      picto_3_url: values.picto_3_url,
      picto_3_name: values.picto_3_name,
      picto_4_url: values.picto_4_url,
      picto_4_name: values.picto_4_name,
      adem_modele_pac: toNullable(values.adem_modele_pac),
      adem_agrement_modele: toNullable(values.adem_agrement_modele),
      regulation_mode: toNullable(values.regulation_mode),
      description_synthese: values.description_synthese,
      description_bc: values.description_bc,
      lang,
      ...detailPayload,
    };

    setIsSaving(true);
    try {
      await updateHeatpump({ url, data: payload }).unwrap();
      onSaved?.();
      onClose();
    } catch {
      // handled by api interceptor
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Popup
      title={t("Infos")}
      onClose={onClose}
      portal
      fixSize="large"
      closeOnOutsideClick
      commandPanelProps={{
        additionalElement: (
          <group data-justify="right" data-gap="10">
            <Button type="button" onClick={onClose} disabled={isSaving}>
              {t("Back")}
            </Button>
            <Button type="button" primary onClick={handleSave} disabled={isSaving}>
              {t("Update")}
            </Button>
          </group>
        ),
      }}
    >
      <group data-direction="column" data-gap="20" data-space="40" data-space-horizontal="40" data-space-top="5" data-height="autofit">
        <group data-gap="20">
          <group data-length="forcefit" data-fit="1">
            <Input dataLength="autofit" label={t("reference")} name="heatpump_reference" control={control} type="text" size="large" required />
          </group>
          <group data-length="forcefit" data-fit="1">
            <Input dataLength="autofit" label={t("reference_def")} name="reference_def" type="text" size="large" disabled defaultValue={String(row?.reference_def ?? "")} />
          </group>
        </group>

        <group data-gap="20">
          <group data-length="forcefit" data-fit="1">
            <Input dataLength="autofit" label={t("heatpump_code")} name="heatpump_code" control={control} type="text" size="large" required />
          </group>
          <group data-length="forcefit" data-fit="1">
            <Input dataLength="autofit" label={t("code")} name="code" type="text" size="large" disabled defaultValue={String(row?.code ?? "")} />
          </group>
        </group>

        <group data-gap="20">
          <group data-length="forcefit" data-fit="1">
            <Input dataLength="autofit" label={t("price")} name="heatpump_price" control={control} type="text" size="large" required />
          </group>
          <group data-length="forcefit" data-fit="1">
            <Input dataLength="autofit" label={t("heatpump_ecoprice")} name="heatpump_ecoprice" control={control} type="text" size="large" required />
          </group>
        </group>

        <group data-gap="20">
          <group data-length="forcefit" data-fit="1">
            <Input dataLength="autofit" label={t("photo")} name="heatpump_photo" control={control} type="text" size="large" />
          </group>
          <group data-length="forcefit" data-fit="1">
            <Input dataLength="autofit" label={t("certif_hp_k")} name="certif_hp_k" control={control} type="text" size="large" />
          </group>
        </group>

        <group data-gap="20">
          <group data-length="forcefit" data-fit="1">
            <Input dataLength="autofit" label={t("photo_ambiance")} name="photo_ambiance" control={control} type="text" size="large" />
          </group>
          <group data-length="forcefit" data-fit="1">
            <Input dataLength="autofit" label={t("prod_europ")} name="prod_europ" control={control} type="text" size="large" />
          </group>
        </group>

        <group data-gap="20">
          <group data-length="forcefit" data-fit="1">
            <Input dataLength="autofit" label={t("regulation_hp")} name="regulation_hp" control={control} type="text" size="large" />
          </group>
          <group data-length="forcefit" data-fit="1">
            <Input dataLength="autofit" label={t("regulation_hp_s")} name="regulation_hp_s" control={control} type="text" size="large" />
          </group>
        </group>

        <group data-gap="20">
          <group data-length="forcefit" data-fit="1">
            <Input dataLength="autofit" label={t("regulation_hp_t")} name="regulation_hp_t" control={control} type="text" size="large" />
          </group>
          <group data-length="forcefit" data-fit="1">
            <Input dataLength="autofit" label={t("regulation_hp_s_t")} name="regulation_hp_s_t" control={control} type="text" size="large" />
          </group>
        </group>

        <group data-gap="20">
          <group data-length="forcefit" data-fit="1">
            <Input dataLength="autofit" label={t("top_right_synth")} name="top_right_synth" control={control} type="text" size="large" />
          </group>
        </group>

        <group data-gap="20">
          <group data-length="forcefit" data-fit="1">
            <Input dataLength="autofit" label={t("picto_1_url")} name="picto_1_url" control={control} type="text" size="large" />
          </group>
          <group data-length="forcefit" data-fit="1">
            <Input dataLength="autofit" label={t("picto_1_name")} name="picto_1_name" control={control} type="text" size="large" />
          </group>
        </group>

        <group data-gap="20">
          <group data-length="forcefit" data-fit="1">
            <Input dataLength="autofit" label={t("picto_2_url")} name="picto_2_url" control={control} type="text" size="large" />
          </group>
          <group data-length="forcefit" data-fit="1">
            <Input dataLength="autofit" label={t("picto_2_name")} name="picto_2_name" control={control} type="text" size="large" />
          </group>
        </group>

        <group data-gap="20">
          <group data-length="forcefit" data-fit="1">
            <Input dataLength="autofit" label={t("picto_3_url")} name="picto_3_url" control={control} type="text" size="large" />
          </group>
          <group data-length="forcefit" data-fit="1">
            <Input dataLength="autofit" label={t("picto_3_name")} name="picto_3_name" control={control} type="text" size="large" />
          </group>
        </group>

        <group data-gap="20">
          <group data-length="forcefit" data-fit="1">
            <Input dataLength="autofit" label={t("picto_4_url")} name="picto_4_url" control={control} type="text" size="large" />
          </group>
          <group data-length="forcefit" data-fit="1">
            <Input dataLength="autofit" label={t("picto_4_name")} name="picto_4_name" control={control} type="text" size="large" />
          </group>
        </group>

        <group data-gap="20">
          <group data-length="forcefit" data-fit="1">
            <Select
              dataLength="autofit"
              label={t("recommended_HP")}
              name="heatpump_recommended"
              control={control as any}
              options={yesNoOptions}
              size="large"
            />
          </group>
          <group data-length="forcefit" data-fit="1">
            <Input dataLength="autofit" label={t("PRIO_HP")} name="heatpump_prio" control={control} type="number" size="large" minValue={1} maxValue={5} />
          </group>
        </group>

        <group data-gap="20">
          <group data-length="forcefit" data-fit="1">
            <Input dataLength="autofit" label={t("elec_schema")} name="elec_schema" control={control} type="text" size="large" />
          </group>
          <group data-length="forcefit" data-fit="1">
            <Input dataLength="autofit" label={t("hydrau_schema")} name="hydrau_schema" control={control} type="text" size="large" />
          </group>
        </group>

        <group data-gap="20">
          <group data-length="forcefit" data-fit="1">
            <Input dataLength="autofit" label={t("adem_modele_pac")} name="adem_modele_pac" control={control} type="number" size="large" />
          </group>
          <group data-length="forcefit" data-fit="1">
            <Input dataLength="autofit" label={t("adem_agrement_modele")} name="adem_agrement_modele" control={control} type="number" size="large" />
          </group>
        </group>

        <group data-gap="20">
          <group data-length="forcefit" data-fit="1">
            <Input dataLength="autofit" label={t("regulation_mode")} name="regulation_mode" control={control} type="text" size="large" />
          </group>
        </group>

        <group data-gap="20">
          <group data-length="forcefit" data-fit="1">
            <Input dataLength="autofit" label={t("description_synthese")} name="description_synthese" control={control} type="text" size="large" />
          </group>
        </group>

        <group data-gap="20">
          <group data-length="forcefit" data-fit="1">
            <Input dataLength="autofit" label={t("description_btoc")} name="description_bc" control={control} type="textarea" size="large" />
          </group>
        </group>

        <group data-gap="20">
          <group data-length="forcefit" data-fit="1">
            <Input dataLength="autofit" label={t("description_btob")} name="description" control={control} type="textarea" size="large" required />
          </group>
        </group>

        <group data-gap="20" data-align="start">
          <group data-direction="column" data-fit="1">
            {/* <text data-weight="700">{t("Technical_data_1")}</text> */}
            {DETAIL_FIELDS_LEFT.map((field) => (
              <group key={field} data-gap="20" data-space-vertical="10">
                <group data-length="forcefit" data-fit="1">
                  <Input dataLength="autofit" label={t(field)} name={field} control={control} type="text" size="large" />
                </group>
              </group>
            ))}
          </group>

          <group data-direction="column" data-fit="1">
            {/* <text data-weight="700">{t("Technical_data_2")}</text> */}
            {DETAIL_FIELDS_RIGHT.map((field) => (
              <group key={field} data-gap="20" data-space-vertical="10">
                <group data-length="forcefit" data-fit="1">
                  <Input dataLength="autofit" label={t(field)} name={field} control={control} type="text" size="large" />
                </group>
              </group>
            ))}
          </group>
        </group>

        <group data-direction="column" data-gap="8" data-space-top="10">
          <group data-justify="space-between" data-align="center" data-gap="10">
            <text data-weight="500" data-text-size="x-large">{t("Heatpump documents")}</text>
            <div //NOSONAR - intentional div-based icon control per project UX pattern
                className="button primary"
                role="button"
                tabIndex={0}
                aria-label="Add"
                onClick={openCreateHpDoc}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") openCreateHpDoc();
                }}
              >
                <icon>add</icon>
            </div>
          </group>

          <Table
            size="small"
            pagination={false}
            dataSource={hpDocs.map((doc, index) => ({
              key: String(doc.id ?? `new-${index}`),
              ...doc,
            }))}
            columns={[
              { title: t("Title"), dataIndex: "title", key: "title" },
              { title: t("Link"), dataIndex: "link", key: "link" },
              { title: t("Type"), dataIndex: "category", key: "category", width: 120 },
              {
                title: t("Action"),
                key: "action",
                width: 120,
                render: (_: unknown, record: HpDocRecord) => (
                  <group data-wrap="no" data-gap="10">
                    <Button type="button" mini onClick={() => openEditHpDoc(record)}>
                      <icon>edit</icon>
                    </Button>
                    <Button
                      type="button"
                      mini
                      {...{ "data-color": "error-color" }}
                      onClick={() => handleDeleteHpDoc(record.id)}
                      disabled={isDeletingHpDoc}
                    >
                      <icon>delete</icon>
                    </Button>
                  </group>
                ),
              },
            ]}
            locale={{ emptyText: t("No documents") }}
          />
        </group>

        <group data-direction="column" data-gap="8" data-space-top="10">
          <group data-justify="space-between" data-align="center" data-gap="10">
            <text data-weight="500" data-text-size="x-large">{t("Compatible_accessories")}</text>
            <div //NOSONAR - intentional div-based icon control per project UX pattern
                className="button primary"
                role="button"
                tabIndex={0}
                aria-label={showCompatibleSelector ? t("Back") : t("Add")}
                onClick={() => setShowCompatibleSelector((v) => !v)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") setShowCompatibleSelector((v) => !v);
                }}
              >
                <icon>{showCompatibleSelector ? "arrow_back" : "add"}</icon>
            </div>
          </group>

          {showCompatibleSelector && (
            <AntSelect
              mode="multiple"
              style={{ width: "100%" }}
              value={compatibleAccessoryIds}
              options={availableAccessoryOptions}
              onChange={(values) => setCompatibleAccessoryIds(values.map(String))}
              showSearch
              optionFilterProp="label"
            />
          )}

          <Table
            size="small"
            pagination={false}
            dataSource={compatibleRows}
            columns={[
              { title: t("code"), dataIndex: "code", key: "code", width: 200 },
              { title: t("reference"), dataIndex: "reference", key: "reference" },
              {
                title: t("Action"),
                key: "action",
                width: 120,
                render: (_: unknown, record: { id: string }) => (
                  <group data-wrap="no" data-gap="10">
                    <Button
                      type="button"
                      mini
                      {...{ "data-color": "error-color" }}
                      onClick={() => handleDeleteCompatible(record.id)}
                    >
                      <icon>delete</icon>
                    </Button>
                  </group>
                ),
              },
            ]}
            locale={{ emptyText: t("No compatible accessories") }}
          />
        </group>
      </group>

      <Modal
        title={currentHpDoc?.id == null ? t("Add") : t("Edit")}
        open={isHpDocModalOpen}
        onCancel={closeHpDocModal}
        onOk={handleHpDocSubmit}
        okText={t("Done")}
        cancelText={t("Back")}
        confirmLoading={isCreatingHpDoc || isUpdatingHpDoc}
      >
        <group data-direction="column" data-gap="12">
          <group data-direction="column" data-gap="4">
            <text>{t("Title")}</text>
            <AntInput
              value={currentHpDoc?.title ?? ""}
              onChange={(e) =>
                setCurrentHpDoc((prev) => ({
                  id: prev?.id,
                  hp: prev?.hp,
                  title: e.target.value,
                  link: prev?.link,
                  category: prev?.category,
                  environnement_id: prev?.environnement_id,
                }))
              }
            />
            {hpDocErrors.title && <text data-color="error-color">{hpDocErrors.title}</text>}
          </group>

          <group data-direction="column" data-gap="4">
            <text>{t("Link")}</text>
            <AntInput
              value={currentHpDoc?.link ?? ""}
              onChange={(e) =>
                setCurrentHpDoc((prev) => ({
                  id: prev?.id,
                  hp: prev?.hp,
                  title: prev?.title,
                  link: e.target.value,
                  category: prev?.category,
                  environnement_id: prev?.environnement_id,
                }))
              }
            />
            {hpDocErrors.link && <text data-color="error-color">{hpDocErrors.link}</text>}
          </group>

          <group data-direction="column" data-gap="4">
            <text>{t("Category")}</text>
            <AntSelect
              value={currentHpDoc?.category}
              options={HP_DOC_CATEGORY_OPTIONS}
              onChange={(value) =>
                setCurrentHpDoc((prev) => ({
                  id: prev?.id,
                  hp: prev?.hp,
                  title: prev?.title,
                  link: prev?.link,
                  category: String(value),
                  environnement_id: prev?.environnement_id,
                }))
              }
            />
            {hpDocErrors.category && <text data-color="error-color">{hpDocErrors.category}</text>}
          </group>
        </group>
      </Modal>
    </Popup>
  );
};

export default HpEditPopup;
